import {
  type Canvas,
  type FabricObject,
  Group,
  Line,
  Point,
  Polygon,
  Polyline,
  Triangle,
  util,
} from "fabric";
import {
  type CountingLineEventType,
  type LineLabelDefs,
  type LineShapeGroup,
  type ZoneShapeGroup,
  ARROW_HEIGHT,
  ARROW_TRI_WIDTH,
  ARROW_TRI_HEIGHT,
  BORDER_COLOR,
  CORNER_COLOR,
  CORNER_STROKE_COLOR,
  LINE_DIR_LABEL_STYLE,
  LINE_NAME_LABEL_STYLE,
  LINE_STROKE,
  LINE_STROKE_WIDTH,
  ZONE_FILL,
  ZONE_LABEL_STYLE,
  ZONE_STROKE,
  setShapeGroup,
  getShapeGroup,
} from "./constants";
import {
  createLabelGroup,
  getDynamicLineLabelStyle,
} from "./canvas-helpers";

// --- Direction arrow + label builders (PO QA 3차 y) ---
// 도면 라인에도 입/퇴장 방향 화살표 + 라벨 표시.

function createArrowGroup(
  px: number,
  py: number,
  angleDeg: number,
  stroke: string,
): Group {
  const stick = new Line([0, -1, 0, ARROW_HEIGHT], {
    originX: "center",
    originY: "top",
    stroke,
    strokeWidth: LINE_STROKE_WIDTH,
    objectCaching: false,
  });
  const tri = new Triangle({
    width: ARROW_TRI_WIDTH,
    height: ARROW_TRI_HEIGHT,
    fill: stroke,
    originX: "center",
    originY: "bottom",
    left: 0,
    top: ARROW_HEIGHT - 0.5,
    angle: 180,
    objectCaching: false,
  });
  return new Group([stick, tri], {
    left: px,
    top: py,
    originX: "center",
    originY: "top",
    angle: angleDeg,
    selectable: false,
    evented: false,
    objectCaching: false,
  });
}

function getArrowTipPoint(arrowGroup: Group): { x: number; y: number } {
  const angleDeg = arrowGroup.angle ?? 0;
  const rad = (angleDeg * Math.PI) / 180;
  const ax = arrowGroup.left ?? 0;
  const ay = arrowGroup.top ?? 0;
  const tipDist = ARROW_HEIGHT + ARROW_TRI_HEIGHT;
  return {
    x: ax - Math.sin(rad) * tipDist,
    y: ay + Math.cos(rad) * tipDist,
  };
}

function getPerpendicularArrowAngles(
  p0: { x: number; y: number },
  p1: { x: number; y: number },
): [number, number] {
  const dx = p1.x - p0.x;
  const dy = p1.y - p0.y;
  const leftAngle = Math.atan2(dy, dx) * (180 / Math.PI);
  const rightAngle = Math.atan2(-dy, -dx) * (180 / Math.PI);
  return [leftAngle, rightAngle];
}

/**
 * 도면 라인의 방향 화살표 + 입/퇴장 라벨 + 이름 라벨 빌더.
 * @param rp0 group center-relative 좌표 (group 자식으로 쓸 때)
 * @param rp1 group center-relative 좌표
 * @returns relative-coord 객체 배열 (group 자식)
 */
export function buildLineDecorationObjects(
  rp0: { x: number; y: number },
  rp1: { x: number; y: number },
  eventType: CountingLineEventType,
  _lineName: string,
  lineLabelDefs: LineLabelDefs,
  dynStyle: { fontSize: number } = { fontSize: 12 },
): FabricObject[] {
  const [leftAngleDeg, rightAngleDeg] = getPerpendicularArrowAngles(rp0, rp1);

  const dx = rp1.x - rp0.x;
  const dy = rp1.y - rp0.y;
  const lineLen = Math.sqrt(dx * dx + dy * dy) || 1;
  const inset = Math.min(lineLen * 0.15, 20);
  const ux = dx / lineLen;
  const uy = dy / lineLen;
  const leftAngleRad = (leftAngleDeg * Math.PI) / 180;
  const rightAngleRad = (rightAngleDeg * Math.PI) / 180;
  const leftArrowPt = { x: rp0.x + ux * inset, y: rp0.y + uy * inset };
  const rightArrowPt = { x: rp1.x - ux * inset, y: rp1.y - uy * inset };

  const leftArrow = createArrowGroup(leftArrowPt.x, leftArrowPt.y, leftAngleDeg, LINE_STROKE);
  const rightArrow = createArrowGroup(rightArrowPt.x, rightArrowPt.y, rightAngleDeg, LINE_STROKE);

  // 방향 규약: coordinates[0]=입장, coordinates[1]=퇴장 (SnapshotCanvas 와 동일, 좌표 순서로 결정)
  const baseLabels = lineLabelDefs[eventType] ?? lineLabelDefs.default;
  const labelDefs = { left: baseLabels.right, right: baseLabels.left };
  const LABEL_GAP = 20;
  const leftTip = getArrowTipPoint(leftArrow);
  const leftLabel = createLabelGroup(
    labelDefs.left,
    leftTip.x - Math.sin(leftAngleRad) * LABEL_GAP,
    leftTip.y + Math.cos(leftAngleRad) * LABEL_GAP,
    { originX: "center", originY: "center", ...LINE_DIR_LABEL_STYLE, ...dynStyle },
  );
  const rightTip = getArrowTipPoint(rightArrow);
  const rightLabel = createLabelGroup(
    labelDefs.right,
    rightTip.x - Math.sin(rightAngleRad) * LABEL_GAP,
    rightTip.y + Math.cos(rightAngleRad) * LABEL_GAP,
    { originX: "center", originY: "center", ...LINE_DIR_LABEL_STYLE, ...dynStyle },
  );

  return [leftArrow, rightArrow, leftLabel, rightLabel];
}

/** world-coord 버전 — canvas 에 직접 add 가능. 편집(coordinate) 모드용. */
export function buildWorldLineDecorationObjects(
  p0: { x: number; y: number },
  p1: { x: number; y: number },
  eventType: CountingLineEventType,
  lineName: string,
  lineLabelDefs: LineLabelDefs,
  dynStyle: { fontSize: number } = { fontSize: 12 },
): FabricObject[] {
  const cx = (p0.x + p1.x) / 2;
  const cy = (p0.y + p1.y) / 2;
  const rp0 = { x: p0.x - cx, y: p0.y - cy };
  const rp1 = { x: p1.x - cx, y: p1.y - cy };
  const decorations = buildLineDecorationObjects(rp0, rp1, eventType, lineName, lineLabelDefs, dynStyle);
  for (const obj of decorations) {
    obj.set({ left: (obj.left ?? 0) + cx, top: (obj.top ?? 0) + cy });
    obj.setCoords();
  }
  return decorations;
}

export function getLineGroupEndpoints(group: Group): { x: number; y: number }[] {
  const polyline = group.getObjects()[0] as Polyline;
  const pts = polyline.points!;
  const plMatrix = polyline.calcTransformMatrix();
  return pts.map((pt) => {
    const world = util.transformPoint(new Point(pt.x, pt.y), plMatrix);
    return { x: world.x, y: world.y };
  });
}

export function rebuildLineGroup(
  canvas: Canvas,
  sg: LineShapeGroup,
  worldPts: { x: number; y: number }[],
  shapeObjectsRef: React.MutableRefObject<FabricObject[]>,
  _lineLabelDefs?: LineLabelDefs,
) {
  const oldGroup = sg.fabricGroup;
  canvas.remove(oldGroup);
  const idx = shapeObjectsRef.current.indexOf(oldGroup);
  if (idx !== -1) shapeObjectsRef.current.splice(idx, 1);

  const p0 = worldPts[0];
  const p1 = worldPts[1];
  const cx = (p0.x + p1.x) / 2;
  const cy = (p0.y + p1.y) / 2;
  const rp0 = { x: p0.x - cx, y: p0.y - cy };
  const rp1 = { x: p1.x - cx, y: p1.y - cy };

  const polyline = new Polyline([rp0, rp1], {
    stroke: LINE_STROKE,
    strokeWidth: LINE_STROKE_WIDTH,
    // PO QA 2차 r ①: 캔버스/이미지 zoom 무관하게 항상 동일 시각 두께 (스냅샷과 동일 비율 유지)
    strokeUniform: true,
    fill: "transparent",
    selectable: false,
    evented: false,
    objectCaching: false,
  });

  // r ②: 라인이 라벨보다 짧거나 비슷하면 라벨 숨김
  const dxx = p1.x - p0.x;
  const dyy = p1.y - p0.y;
  const lineLen = Math.sqrt(dxx * dxx + dyy * dyy);
  const dyn = getDynamicLineLabelStyle(canvas);
  const nameLabel = createLabelGroup(sg.lineName ?? "", 0, 0, { ...LINE_NAME_LABEL_STYLE, ...dyn });
  const showLabel = lineLen > (nameLabel.width ?? 0) * 1.2;
  // (F12) 도면에서는 입/퇴장 방향 라벨 제거 — 스냅샷에서만 노출
  const groupChildren: FabricObject[] = showLabel ? [polyline, nameLabel] : [polyline];

  const newGroup = new Group(groupChildren, {
    left: cx,
    top: cy,
    originX: "center",
    originY: "center",
    selectable: true,
    evented: true,
    hasControls: true,
    hasBorders: true,
    borderColor: BORDER_COLOR,
    borderScaleFactor: 2,
    cornerColor: CORNER_COLOR,
    cornerStrokeColor: CORNER_STROKE_COLOR,
    cornerStyle: "rect",
    cornerSize: 8,
    transparentCorners: false,
    lockScalingFlip: true,
    objectCaching: false,
    subTargetCheck: false,
  });

  setShapeGroup(newGroup, sg);
  sg.fabricGroup = newGroup;
  canvas.add(newGroup);
  shapeObjectsRef.current.push(newGroup);
  canvas.setActiveObject(newGroup);
  canvas.requestRenderAll();
}

export function rebuildZonePolygon(
  canvas: Canvas,
  sg: ZoneShapeGroup,
  worldPts: { x: number; y: number }[],
  shapeObjectsRef: React.MutableRefObject<FabricObject[]>,
) {
  const oldObjs = shapeObjectsRef.current.filter((o) => getShapeGroup(o) === sg);
  for (const obj of oldObjs) {
    canvas.remove(obj);
    const idx = shapeObjectsRef.current.indexOf(obj);
    if (idx !== -1) shapeObjectsRef.current.splice(idx, 1);
  }

  const cx = worldPts.reduce((s, p) => s + p.x, 0) / worldPts.length;
  const cy = worldPts.reduce((s, p) => s + p.y, 0) / worldPts.length;
  const relPts = worldPts.map((p) => ({ x: p.x - cx, y: p.y - cy }));

  const newPoly = new Polygon(relPts, {
    originX: "center",
    originY: "center",
    fill: ZONE_FILL,
    stroke: ZONE_STROKE,
    strokeWidth: 2,
    strokeUniform: true,
    selectable: true,
    evented: true,
    hasControls: true,
    hasBorders: true,
    borderColor: BORDER_COLOR,
    borderScaleFactor: 2,
    cornerColor: CORNER_COLOR,
    cornerStrokeColor: CORNER_STROKE_COLOR,
    cornerStyle: "rect",
    cornerSize: 8,
    transparentCorners: false,
    lockScalingFlip: true,
    objectCaching: false,
  });
  newPoly.set({ left: cx + newPoly.pathOffset.x, top: cy + newPoly.pathOffset.y });
  newPoly.setCoords();
  setShapeGroup(newPoly, sg);
  sg.polygon = newPoly;

  // r: 구역 라벨도 도면 bg 비례 동적 fontSize + collision 숨김
  const dynZone = getDynamicLineLabelStyle(canvas);
  const nameLabel = createLabelGroup(sg.zoneName ?? "", cx, cy, { ...ZONE_LABEL_STYLE, ...dynZone });
  setShapeGroup(nameLabel, sg);

  // 구역 바운딩 박스가 라벨보다 작으면 라벨 숨김 (canvas에는 항상 추가, visible만 토글)
  const polyW = newPoly.width ?? 0;
  const polyH = newPoly.height ?? 0;
  const labelW = nameLabel.width ?? 0;
  const labelH = nameLabel.height ?? 0;
  const showZoneLabel = polyW > labelW * 1.2 && polyH > labelH * 1.2;
  if (!showZoneLabel) nameLabel.set({ visible: false });

  canvas.add(newPoly);
  canvas.add(nameLabel);
  shapeObjectsRef.current.push(newPoly, nameLabel);
  canvas.setActiveObject(newPoly);
  canvas.requestRenderAll();
}
